import { FC, ReactNode, useEffect, useRef } from "react";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@capacit-com/lara-law-shared-ui";
import {
  GripVertical,
  Pencil,
  Trash2,
  MoreVertical,
  BookmarkPlus,
  Copy,
} from "lucide-react";
import { WidgetInstance } from "@/models/dashboard/Dashboard";
import { cn } from "@/lib/utils";
import { getWidgetTypeInfo } from "../lib/widgetCatalog";

interface WidgetWrapperProps {
  widget: WidgetInstance;
  children: ReactNode;
  isEditMode: boolean;
  /** The editor panel is open on this widget (LAR-726). */
  isBeingEdited?: boolean;
  /** The editor panel is open on another widget. */
  isDimmed?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onSaveAsTemplate?: () => void;
  onCopyToOtherDashboard?: () => void;
}

const WidgetWrapper: FC<WidgetWrapperProps> = ({
  widget,
  children,
  isEditMode,
  isBeingEdited = false,
  isDimmed = false,
  onEdit,
  onDelete,
  onSaveAsTemplate,
  onCopyToOtherDashboard,
}) => {
  const widgetName = widget.name.trim() || getWidgetTypeInfo(widget.type).label;
  const cardRef = useRef<HTMLDivElement>(null);

  // A widget picked in the gallery lands at the bottom of the grid; bring the
  // edited widget into view so the admin sees what the panel is changing.
  useEffect(() => {
    if (isBeingEdited) cardRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [isBeingEdited]);

  return (
    <Card
      ref={cardRef}
      className={cn(
        /* Tables inside widgets sit on bg-card; web-app styles .table-embed-card in globals (not shared-ui) */
        "table-embed-card h-full flex flex-col overflow-hidden py-0 gap-0 group/widget transition-opacity",
        isEditMode && "ring-2 ring-primary/20",
        isBeingEdited && "ring-primary",
        // Still reachable: its pencil switches the panel to it.
        isDimmed && "opacity-60 hover:opacity-100 focus-within:opacity-100"
      )}
      aria-current={isBeingEdited || undefined}
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 py-2 pb-2! px-4 shrink-0 border-b border-border bg-muted/40">
        <div className="flex min-w-0 items-center gap-2">
          {isEditMode && (
            <div className="widget-drag-handle shrink-0 cursor-grab active:cursor-grabbing">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <CardTitle className="truncate text-sm font-medium">
            {widget.name}
          </CardTitle>
          {isBeingEdited && (
            <Badge className="shrink-0 text-[10px] font-semibold uppercase tracking-wide">
              Redigeres
            </Badge>
          )}
          {isEditMode && (
            // The title is the live preview, so on a narrow widget the tag gives way to it.
            <Badge
              variant="outline"
              className="hidden shrink-0 text-xs font-normal text-muted-foreground @sm/card-header:inline-flex"
            >
              {getWidgetTypeInfo(widget.type).label}
            </Badge>
          )}
        </div>

        {isEditMode ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onEdit}
              aria-label={`Rediger ${widgetName}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label={`Flere handlinger for ${widgetName}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onCopyToOtherDashboard}>
                  <Copy className="h-4 w-4 mr-2" />
                  Kopier til dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onSaveAsTemplate}>
                  <BookmarkPlus className="h-4 w-4 mr-2" />
                  Gem som skabelon
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Slet widget
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : onCopyToOtherDashboard ? (
          // Read-mode: show a minimal menu with only the copy action
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover/widget:opacity-100 transition-opacity"
                aria-label={`Flere handlinger for ${widgetName}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onCopyToOtherDashboard}>
                <Copy className="h-4 w-4 mr-2" />
                Kopier til dashboard
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </CardHeader>
      <CardContent className="flex-1 min-h-0 p-0">{children}</CardContent>
    </Card>
  );
};

export default WidgetWrapper;
